import { useMemo, type FC, useCallback } from 'react';
import copy from 'copy-to-clipboard';
import {
  useDisclosure
} from '@chakra-ui/react';
import {
  UiStack,
  UiText,
  uiStyles,
  UiHStack,
  UiButton,
  UiLink,
  UiIconCopy, UiIconArrowSquareOut, UiIconX,
} from '@/lib/ui';
import { type Order } from '@/api/registration/order';
import { useEventQuery } from '@/registration/hook/useEventQuery';
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterShareButton,
  XIcon
} from 'react-share';
import { generatePageUrl } from '@/app/pages';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import BaseBlockList from '@/base/BlockList';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import BaseButtonPlainIcon from '@/base/Button/PlainIcon';
import { useNavigate } from 'react-router-dom';

export interface RegistrationDetailsProps {
  order: Order
}

const RegistrationDetails: FC<RegistrationDetailsProps> = ({ order }) => {
  const { isOpen, onToggle } = useDisclosure();
  const { tenantCode, eventId } = useRegisterRoute();
  const { data: event } = useEventQuery(order.eventId);
  const url = useMemo(
    () => window.location.origin + generatePageUrl('RegistrationRegisterEmail', { tenantCode, eventId })
    , [eventId, tenantCode]);
  const title = useMemo(
    () => `I just registered for "${event?.name}"!`
    , [event?.name]);
  const copyInvoiceNumber = useCallback(
    () => { return copy(order.serial); },
    [order.serial]
  );
  const navigate = useNavigate();

  return (
    <>
      <BaseBlockList
        bgColor={'#fff'}
        borderRadius={uiStyles.borderRadius}
        enableDivider={true}
        py={2}
      >
        <UiStack justifyContent={'space-between'} px={{base: 6, lg: 8}} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Invoice number</UiText>
          <UiHStack
            onClick={copyInvoiceNumber}
          >
            <UiText variant='body1'>{order.serial}</UiText>
            <BaseButtonPlainIcon
              label={'Copy'}
              color={'primary.500'}
              Icon={UiIconCopy}
              {...uiStyles.hover}
            />
          </UiHStack>
          {/*<UiText variant='body1'>{order.serial}</UiText>*/}
        </UiStack>
        <UiStack justifyContent={'space-between'} px={{base: 6, lg: 8}} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Name</UiText>
          <UiText variant='body1'>{order.attendee.name}</UiText>
        </UiStack>
        <UiStack justifyContent={'space-between'} px={{base: 6, lg: 8}} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Email</UiText>
          <UiText variant='body1'>{order.attendee.email}</UiText>
        </UiStack>
        <UiStack justifyContent={'space-between'} px={{base: 6, lg: 8}} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Event</UiText>
          <UiText variant='body1'>{order.event}</UiText>
        </UiStack>
        <UiStack justifyContent={'space-between'} px={{base: 6, lg: 8}} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Total price</UiText>
          <UiText variant='body1'>{order.totalPriceFormat}</UiText>
        </UiStack>
        {/*<UiHStack justifyContent={'space-between'} px={{base: 6, lg: 8}} py={4}>*/}
        {/*  <UiText variant='body1' color={'text.secondary'}>Purchased</UiText>*/}
        {/*  <UiText variant='body1'>{order.purchasedAt ? dayjs(order.purchasedAt).format('DD MMM YYYY, h:mm A') : 'Unpaid'}</UiText>*/}
        {/*</UiHStack>*/}
        <UiStack justifyContent={'space-between'} px={{base: 6, lg: 8}} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Order status</UiText>
          <UiText variant='body1'>{order.status ?? 'N/A'}</UiText>
        </UiStack>
        <UiStack px={{base: 6, lg: 8}} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Purchases</UiText>
          {order.orderItems && order.orderItems.length > 0 ? order.orderItems.map((orderItems, index) => (
            <UiText key={index} variant='body1'>{orderItems.name} - {orderItems.attendeeName}</UiText>
          )) : (
            <UiText variant='body1'>N/A</UiText>
          )}
        </UiStack>

      </BaseBlockList>
      <UiStack alignItems={'flex-end'}>
        <UiButton px={8} size={'lg'} shadow={'base'} colorScheme={'primary'} onClick={() => { return navigate(generatePageUrl('RegistrationRegisterRegistrationDetails', { tenantCode, eventId })); }}>
          View details
        </UiButton>
      </UiStack>
      <BaseDividerHorizontal height={2} />
      <UiStack spacing={6}>
        {!!event?.websiteUrl && (
          <UiHStack justifyContent={'flex-end'}>
            <UiLink href={event?.websiteUrl ?? undefined} target={'_blank'}>
              <UiHStack
                spacing={4}
                px={6}
                py={4}
                borderRadius={uiStyles.borderRadius}
                bgColor={'#fff'}
              >
                <UiText>{order.event} website</UiText>
                <UiStack>
                  <UiIconArrowSquareOut size={'2xl'} color={'primary.500'}/>
                </UiStack>
              </UiHStack>
            </UiLink>
          </UiHStack>
        )}
        <UiHStack justifyContent={'flex-end'} spacing={4}>
          <LinkedinShareButton url={url} title={title}>
            <LinkedinIcon size={40} round={true} />
          </LinkedinShareButton>
          <TwitterShareButton url={url} title={title}>
            <XIcon size={40} round={true} />
          </TwitterShareButton>
          <FacebookShareButton url={url}>
            <FacebookIcon size={40} round={true} />
          </FacebookShareButton>
        </UiHStack>
      </UiStack>
    </>
  );
};

export default RegistrationDetails;
